import React, { useState, useEffect } from 'react';
import { Card, CardBody, Select, SelectItem, Textarea } from '@heroui/react';
import CustomInput from '../../components/CustomInput';
import CustomButton from '../../components/CustomButton';
import { FaCloudUploadAlt, FaTimes, FaCheckCircle } from 'react-icons/fa';
import {
  supportTicketSchema,
  SupportTicketFormData,
} from '../../../utils/validation/supportTicketSchema';
import { submitSupportTicket } from '../../../service/supportTicketService';
import { useAuth } from '../../../context/AuthContext';

interface SupportTicketFormProps {
  onSuccess?: () => void;
}

// Using the type from validation schema
type FormData = SupportTicketFormData;

const SupportTicketForm: React.FC<SupportTicketFormProps> = ({ onSuccess }) => {
  const { user } = useAuth();

  const [formData, setFormData] = useState<FormData>({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phoneNumber || user?.mobile || user?.phone || '',
    subject: '',
    category: '',
    priority: '',
    description: '',
    attachments: [],
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [fileError, setFileError] = useState<string>('');
  const [submitError, setSubmitError] = useState<string>('');
  const [ticketId, setTicketId] = useState<string>('');

  // Auto-fill user information when available
  useEffect(() => {
    if (!user) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      email: prev.email || user.email || '',
      name: prev.name || user.name || '',
      phone: prev.phone || user.phoneNumber || user.mobile || user.phone || '',
    }));
  }, [user]);

  const categories = [
    { key: 'technical', label: 'Technical Issue' },
    { key: 'billing', label: 'Billing Question' },
    { key: 'feature', label: 'Feature Request' },
    { key: 'general', label: 'General Question' },
    { key: 'membership', label: 'Membership Management' },
  ];

  const priorities = [
    { key: 'low', label: 'Low' },
    { key: 'medium', label: 'Medium' },
    { key: 'high', label: 'High' },
  ];

  const validateForm = async (): Promise<boolean> => {
    try {
      await supportTicketSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof Error && 'inner' in error) {
        const validationErrors: Partial<FormData> = {};
        (error as any).inner.forEach((err: any) => {
          if (err.path) {
            validationErrors[err.path as keyof FormData] = err.message;
          }
        });
        setErrors(validationErrors);
      }
      return false;
    }
  };

  const validateField = async (
    field: keyof FormData,
    value: string,
    updatedData?: FormData
  ) => {
    const dataToValidate = updatedData || { ...formData, [field]: value };

    try {
      await supportTicketSchema.validateAt(field, dataToValidate);
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    } catch (error) {
      if (error instanceof Error) {
        setErrors((prev) => ({ ...prev, [field]: error.message }));
      }
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => {
      const updatedData = { ...prev, [field]: value };

      if (field === 'email') {
        // Validate email immediately so the user sees why submission is blocked
        validateField(field, value, updatedData);
      }

      return updatedData;
    });

    if (field !== 'email' && errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    // Clear previous file error
    setFileError('');

    const validFiles = files.filter((file) => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 2 * 1024 * 1024; // 2MB limit
      return isValidType && isValidSize;
    });

    // Check if adding these files would exceed the limit (3 as per schema)
    if (validFiles.length + (formData.attachments?.length || 0) > 3) {
      setFileError('You can only upload a maximum of 3 images');
      return;
    }

    // Check for invalid file types or sizes
    const invalidFiles = files.filter((file) => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 2 * 1024 * 1024;
      return !isValidType || !isValidSize;
    });

    if (invalidFiles.length > 0) {
      setFileError('Please upload only image files under 2MB each');
      return;
    }

    const newAttachments = [...(formData.attachments || []), ...validFiles];

    // Validate with yup schema
    supportTicketSchema
      .validateAt('attachments', { attachments: newAttachments })
      .then(() => {
        setFormData((prev) => ({
          ...prev,
          attachments: newAttachments,
        }));
        setFileError('');
      })
      .catch((error) => {
        setFileError(error.message);
      });
  };

  const removeAttachment = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((_, i) => i !== index),
    }));
    // Clear file error when removing files
    setFileError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(''); // Clear any previous errors

    try {
      console.log('Submitting support ticket:', formData);

      const response = await submitSupportTicket(formData);

      console.log('Support ticket submitted successfully:', response);

      // Store the ticket ID for display
      setTicketId(response.ticket.id);
      setIsSuccess(true);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error submitting support ticket:', error);

      // Set user-friendly error message
      const errorMessage =
        error.message || 'Failed to submit support ticket. Please try again.';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardBody className="text-center p-8">
            <div className="mb-6">
              <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Ticket Submitted Successfully!
              </h2>
              <p className="text-gray-600 mb-2">
                Thank you for contacting us. We'll get back to you within 24
                hours.
              </p>
              {ticketId && (
                <p className="text-sm text-gray-500 mb-4">
                  Ticket ID:{' '}
                  <span className="font-mono font-semibold">{ticketId}</span>
                </p>
              )}
            </div>
            <CustomButton
              label="Go Back to Home"
              color="primary"
              fullWidth
              onPress={() => {
                window.location.href = '/home';
              }}
            />
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <Card className="shadow-lg">
          <CardBody className="p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                HirePanther Support Ticket
              </h1>
              <p className="text-gray-600">
                Please provide the details of your problem.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Top Row - Name, Email, Phone */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div>
                  <CustomInput
                    label="Name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onValueChange={(value) => handleInputChange('name', value)}
                    isRequired
                    isInvalid={!!errors.name}
                    errorMessage={errors.name}
                  />
                </div>
                <div>
                  <CustomInput
                    label="E-mail"
                    type="email"
                    placeholder="ex: myname@example.com"
                    value={formData.email}
                    onValueChange={(value) => handleInputChange('email', value)}
                    isRequired
                    isInvalid={!!errors.email}
                    errorMessage={errors.email}
                  />
                </div>
                <div>
                  <CustomInput
                    label="Phone Number"
                    type="tel"
                    placeholder="Enter your phone number (optional)"
                    value={formData.phone}
                    onValueChange={(value) => handleInputChange('phone', value)}
                    isInvalid={!!errors.phone}
                    errorMessage={errors.phone}
                  />
                </div>
              </div>

              {/* Second Row - Subject, Category, Priority */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div>
                  <CustomInput
                    label="Subject"
                    type="text"
                    placeholder="e.g., Login Issue, Payment Problem, Account Access"
                    value={formData.subject}
                    onValueChange={(value) =>
                      handleInputChange('subject', value)
                    }
                    isRequired
                    isInvalid={!!errors.subject}
                    errorMessage={errors.subject}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <Select
                    placeholder="Select a category"
                    selectedKeys={formData.category ? [formData.category] : []}
                    onSelectionChange={(keys) => {
                      const selectedKey = Array.from(keys)[0] as string;
                      handleInputChange('category', selectedKey);
                    }}
                    isInvalid={!!errors.category}
                    classNames={{
                      trigger:
                        'border !border-gray-300 rounded-md !bg-white hover:!border-gray-300 focus-within:!border-gray-300 focus-within:!bg-white outline-none focus:outline-none',
                      value: 'text-xs',
                      listbox: 'text-xs',
                    }}
                  >
                    {categories.map((category) => (
                      <SelectItem key={category.key}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </Select>
                  {errors.category && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.category}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-2">
                    Priority <span className="text-red-500">*</span>
                  </label>
                  <Select
                    placeholder="Select priority level"
                    selectedKeys={formData.priority ? [formData.priority] : []}
                    onSelectionChange={(keys) => {
                      const selectedKey = Array.from(keys)[0] as string;
                      handleInputChange('priority', selectedKey);
                    }}
                    isInvalid={!!errors.priority}
                    classNames={{
                      trigger:
                        'border !border-gray-300 rounded-md !bg-white hover:!border-gray-300 focus-within:!border-gray-300 focus-within:!bg-white outline-none focus:outline-none',
                      value: 'text-xs',
                      listbox: 'text-xs',
                    }}
                  >
                    {priorities.map((priority) => (
                      <SelectItem key={priority.key}>
                        {priority.label}
                      </SelectItem>
                    ))}
                  </Select>
                  {errors.priority && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.priority}
                    </p>
                  )}
                </div>
              </div>

              {/* Third Row - Description and File Upload Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Description Field */}
                <div className="flex flex-col">
                  <label className="block text-xs text-gray-600 mb-2">
                    Describe the Problem <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    placeholder='Please be specific. For example: "When I try to log in, I get an "Invalid Password" error, but I am sure my password is correct."'
                    value={formData.description}
                    onValueChange={(value) =>
                      handleInputChange('description', value)
                    }
                    isInvalid={!!errors.description}
                    classNames={{
                      input: 'text-xs',
                      inputWrapper:
                        'border !border-gray-300 resize-none !bg-white hover:!border-gray-300 focus-within:!border-gray-300 focus-within:!bg-white outline-none focus:outline-none',
                    }}
                    minRows={8}
                    maxRows={8}
                    className="flex-1"
                  />
                  {errors.description && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.description}
                    </p>
                  )}
                </div>

                {/* File Upload Section */}
                <div className="flex flex-col">
                  <label className="block text-xs text-gray-600 mb-2">
                    Upload Screenshot
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors flex-1 flex flex-col justify-center">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                      disabled={(formData.attachments?.length || 0) >= 3}
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <FaCloudUploadAlt className="text-3xl text-gray-400 mb-2" />
                      <p className="text-sm font-semibold text-gray-700 mb-1">
                        Upload a File
                      </p>
                      <p className="text-xs text-gray-500">
                        Drag and drop files here
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Maximum 3 images, 2MB each
                      </p>
                    </label>
                  </div>

                  {/* Display uploaded files */}
                  {(formData.attachments?.length || 0) > 0 && (
                    <div className="mt-4 space-y-2 max-h-32 overflow-y-auto">
                      {formData.attachments?.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-gray-100 p-2 rounded"
                        >
                          <span className="text-xs text-gray-700 truncate">
                            {file instanceof File ? file.name : 'Unknown file'}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <FaTimes size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* File Error Message */}
                  {fileError && (
                    <p className="text-xs text-red-500 mt-2">{fileError}</p>
                  )}
                </div>
              </div>

              {/* Error Display */}
              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <FaTimes className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Error submitting ticket
                      </h3>
                      <div className="mt-1 text-sm text-red-700">
                        {submitError}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-4">
                <CustomButton
                  type="submit"
                  label="Submit"
                  color="primary"
                  fullWidth
                  size="lg"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                  className="text-white font-semibold py-3"
                  style={{ backgroundColor: '#317690' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a5f7a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#317690';
                  }}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default SupportTicketForm;
